import { useEffect, useMemo, useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import {
  ERROR_STATE_ACTIVE,
  ERROR_STATE_IGNORED,
  ERROR_STATE_RESOLVED,
  errorDetailQuery,
  errorOccurrencesQuery,
  setErrorState,
  type ErrorOccurrence,
} from '../../api/admErrors'
import { shellQuery } from '../../api/shell'
import { Content } from '../../ui/Shell'
import { DataTable, useTableState, type TableColumn } from '../../ui/DataTable'
import { toast } from '../../ui/notify'
import { usePageStyles } from '../../ui/usePageStyles'
import { AdmPageHead } from './AdmPageHead'
import { AdmFilterBar, AdmField, WindowPicker } from './AdmFilterBar'

/**
 * Treapta a treia din ADM -> Errors: adresele din spatele unui singur cod.
 * Mutat din `views/version-3/softsee/adm/errors/detail.php` si din tabelul lui
 * din `public/js/adm.js`.
 *
 * Aici lista se aduce de pe SERVER, pagina cu pagina: un cod de retea poate sta
 * pe patru mii de adrese, si browserul nu e locul in care se afla asta.
 *
 * DOUA SCARI, ACELASI BUTON. „Ignore all" si „Mark fixed" de sus lucreaza pe
 * TOT codul, prin aceleasi filtre prin care se citeste lista; butonul de pe
 * rand lucreaza pe o singura adresa. Cine repara un firewall inchide codul;
 * cine stie ca o singura masina e scoasa din priza inchide randul.
 */

const STYLES = ['css/adm.css']

const STATE_PILL: Record<number, string> = { 1: 'adm-pill-bad', 2: 'adm-pill', 3: 'adm-pill-ok' }

/** Textul unei celule, scapat - `$('<div>').text(x).html()` din adm.js. */
function esc(value: unknown): string {
  const box = document.createElement('div')
  box.textContent = String(value ?? '')
  return box.innerHTML
}

function count(value: number): string {
  return value.toLocaleString('en-US')
}

/**
 * Coloanele listei, in ordinea din `Discovery_errors::getOccurrencesData()` -
 * ea hotaraste si dupa ce se poate ordona.
 */
function occurrenceColumns(canWrite: boolean): TableColumn[] {
  return [
    { index: 0, title: 'Last seen', orderable: true, visible: true, className: 'adm-nowrap', html: (row) => esc(row.last_seen) },
    { index: 1, title: 'Customer', orderable: true, visible: true, html: (row) => esc(row.company_name) },
    { index: 2, title: 'Address', orderable: true, visible: true, className: 'adm-nowrap', html: (row) => esc(row.target_ip) },
    { index: 3, title: 'Relay', orderable: true, visible: true, html: (row) => esc((row as unknown as ErrorOccurrence).relay_name || '—') },
    {
      index: 4,
      title: 'Times',
      orderable: true,
      visible: true,
      className: 'adm-numeric',
      html: (row) => count(Number(row.occurrences ?? 0)),
    },
    {
      index: 5,
      title: 'State',
      orderable: true,
      visible: true,
      html: (row) => {
        const r = row as unknown as ErrorOccurrence
        return `<span class="adm-pill ${STATE_PILL[r.state] ?? ''}">${esc(r.state_label)}</span>`
      },
    },
    {
      index: 6,
      title: '',
      orderable: false,
      visible: true,
      width: 110,
      className: 'text-right adm-nowrap',
      html: (row) => {
        const r = row as unknown as ErrorOccurrence
        if (!canWrite) return ''

        /* Cele doua ale portalului, pe fiecare rand. */
        const buttons = [
          `<button type="button" class="btn btn-warning btn-xs admIgnoreRow" data-id="${r.ID}" title="Ignore"><i class="fa fa-ban"></i></button>`,
          `<button type="button" class="btn btn-success btn-xs admResolveRow" data-id="${r.ID}" title="Mark fixed"><i class="fa fa-check"></i></button>`,
        ]

        /* Si drumul inapoi, pe randurile care nu mai sunt active. Portalul nu-l
           avea fiindca nici nu putea ARATA un rand ignorat: ecranul lui nu avea
           filtru de stare, deci nu se punea problema. De cand se poate ajunge la
           ele, o apasare gresita nu mai are voie sa fie definitiva. */
        if (r.state !== ERROR_STATE_ACTIVE) {
          buttons.push(
            `<button type="button" class="btn btn-default btn-xs admActivateRow" data-id="${r.ID}" title="Report it again"><i class="fa fa-undo"></i></button>`,
          )
        }

        return buttons.join(' ')
      },
    },
  ]
}

export function ErrorDetailPage({ code, days }: { code: string; days: number }) {
  const shellData = useQuery(shellQuery('softsee'))
  usePageStyles(STYLES, shellData.data?.version ?? '')

  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [stateFilter, setStateFilter] = useState(String(ERROR_STATE_ACTIVE))

  /* Se cauta dupa ce omul se opreste din scris: lista vine de pe server, deci
     fiecare litera ar fi insemnat o interogare, si raspunsurile s-ar fi intors
     amestecate. */
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(search.trim()), 250)
    return () => window.clearTimeout(timer)
  }, [search])

  const detail = useQuery(errorDetailQuery(code, days))
  const [tableState, setTableState] = useTableState(`admErrorOccurrences-${code}`, {
    start: 0,
    length: 25,
    order: { column: 0, dir: 'desc' },
    search: '',
  })

  const table = useQuery({
    ...errorOccurrencesQuery(code, {
      draw: 1,
      start: tableState.start,
      length: tableState.length,
      orderColumn: tableState.order.column,
      orderDir: tableState.order.dir,
      search: debounced,
      days,
      state: stateFilter,
    }),
    placeholderData: keepPreviousData,
  })

  const canWrite = detail.data?.canWrite ?? false
  const columns = useMemo(() => occurrenceColumns(canWrite), [canWrite])

  const decide = async (target: { id?: number; code?: string }, state: number) => {
    try {
      const answer = await setErrorState(target, state, days)
      toast(answer.message, 'Discovery errors', 'success')
      void table.refetch()
      void detail.refetch()
    } catch (error) {
      toast((error as Error).message, 'Discovery errors', 'error')
    }
  }

  /* Butoanele de pe rand sunt scrise in HTML-ul celulei, ca in portal: clicul
     se prinde o data, pe tabel. */
  useEffect(() => {
    const host = document.getElementById('admOccurrencesTable')
    if (!host) return
    const onClick = (event: Event) => {
      const target = event.target as HTMLElement
      const ignore = target.closest('.admIgnoreRow')
      const resolve = target.closest('.admResolveRow')
      const activate = target.closest('.admActivateRow')
      if (ignore) void decide({ id: Number(ignore.getAttribute('data-id')) }, ERROR_STATE_IGNORED)
      if (resolve) void decide({ id: Number(resolve.getAttribute('data-id')) }, ERROR_STATE_RESOLVED)
      if (activate) void decide({ id: Number(activate.getAttribute('data-id')) }, ERROR_STATE_ACTIVE)
    }
    host.addEventListener('click', onClick)
    return () => host.removeEventListener('click', onClick)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table.data, days])

  if (detail.isError) {
    const message = (detail.error as Error).message

    return (
      <Content>
        <div id="content">
          <div className="adm-empty-inline">
            <i className="fa fa-info-circle"></i> {message}
            <div style={{ marginTop: 8 }}>
              <a className="btn btn-default btn-sm" href={`/app/adm/errors?days=${days}`}>
                Back
              </a>
            </div>
          </div>
        </div>
      </Content>
    )
  }

  const row = detail.data?.code

  return (
    <Content>
      <div id="content">
        <AdmPageHead
          tone="red-intense"
          icon="fa-times-circle"
          title={code}
          note="every address this failure happened on, and what to do about it"
          stats={[
            { value: count(row?.occurrences ?? 0), label: 'occurrences', tone: 'red-intense' },
            { value: count(row?.companies ?? 0), label: 'customers', tone: 'grey' },
          ]}
        />

        <div className="adm-bar">
          <div className="adm-field adm-field-wide">
            <label>Category</label>
            <div>{row?.category_label ?? ''}</div>
          </div>

          <div className="adm-field">
            <label>Occurrences</label>
            <div>{count(row?.occurrences ?? 0)}</div>
          </div>

          <div className="adm-field">
            <label>Customers affected</label>
            <div>{row?.companies ?? 0}</div>
          </div>

          <div className="adm-field">
            <label>First seen</label>
            <div>{row?.first_seen ?? ''}</div>
          </div>

          <div className="adm-field">
            <label>Last seen</label>
            <div>{row?.last_seen ?? ''}</div>
          </div>

          <span className="adm-spacer"></span>

          {canWrite ? (
            <>
              <button type="button" className="btn btn-warning btn-sm admIgnoreCode" onClick={() => void decide({ code }, ERROR_STATE_IGNORED)}>
                <i className="fa fa-ban"></i> Ignore all
              </button>{' '}
              <button type="button" className="btn btn-success btn-sm admResolveCode" onClick={() => void decide({ code }, ERROR_STATE_RESOLVED)}>
                <i className="fa fa-check"></i> Mark fixed
              </button>{' '}
            </>
          ) : null}

          <a className="btn btn-default btn-sm" href={`/app/adm/errors?days=${days}`}>
            Back
          </a>
        </div>

        {row?.guidance ? (
          /* Codul vine din ServiceNow, ca vocabularul sa fie cel pe care il
             asteapta un evaluator. Propozitia e a noastra: un cod singur ii
             spune omului ca a picat ceva, nu si cine poate repara. */
          <div className="adm-guidance">
            <strong>{code}</strong> — {row.guidance}
          </div>
        ) : null}

        <AdmFilterBar>
          <AdmField label="Search" htmlFor="errSearch" wide>
            <input
              type="text"
              className="form-control input-sm"
              id="errSearch"
              placeholder="customer, address, or message"
              autoComplete="off"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </AdmField>

          <AdmField label="Show" htmlFor="errState">
            <select className="form-control input-sm" id="errState" value={stateFilter} onChange={(event) => setStateFilter(event.target.value)}>
              {(detail.data?.states ?? []).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
              <option value="">Everything</option>
            </select>
          </AdmField>

          <WindowPicker
            windows={detail.data?.windows ?? [1, 7, 30, 90]}
            value={days}
            onChange={(next) => window.location.assign(`/app/adm/errors/${encodeURIComponent(code)}?days=${next}`)}
          />
        </AdmFilterBar>

        <div id="admOccurrencesTable">
          <DataTable
            id="admOccurrences"
            columns={columns}
            rows={(table.data?.data ?? []) as unknown as Record<string, unknown>[]}
            total={table.data?.recordsTotal ?? 0}
            fetching={table.isFetching}
            error={table.isError ? (table.error as Error).message : null}
            onRetry={() => void table.refetch()}
            state={tableState}
            onState={setTableState}
            rowId={(r) => String(r.DT_RowId ?? r.ID)}
            emptyHint="Nothing recorded for this code in the selected period."
          />
        </div>
      </div>
    </Content>
  )
}
